import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Coins, Users, Plus, KeyRound, ShieldOff, Save, Download, Loader2 } from "lucide-react";
import { CURRENCY_SYMBOLS } from "@/lib/format";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [fx, setFx] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingFx, setSavingFx] = useState(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [enrollForm, setEnrollForm] = useState({ name: "", email: "", role: "editor" });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const reqs = [api.get("/fx")];
      reqs.push(api.get("/auth/users").catch(() => ({ data: [] })));
      const [f, u] = await Promise.all(reqs);
      setFx(f.data); setUsers(u.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateFx = async (ccy, val) => {
    setSavingFx(ccy);
    try { await api.put("/fx", { currency: ccy, rate_to_inr: Number(val) }); toast.success(`${ccy} rate updated`); }
    catch { toast.error("Could not update rate"); }
    finally { setSavingFx(null); }
  };

  const enroll = async () => {
    if (!enrollForm.name.trim() || !enrollForm.email.trim()) { toast.error("Name and email required"); return; }
    setBusy(true);
    try { await api.post("/auth/users", enrollForm); toast.success("User enrolled. They can now generate their key on the login screen."); setEnrollOpen(false); setEnrollForm({ name: "", email: "", role: "editor" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not enrol"); }
    finally { setBusy(false); }
  };

  const provision = async (u) => {
    try {
      const { data } = await api.post("/auth/provision-token", { email: u.email });
      const blob = new Blob([data.token], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = url; a.download = data.filename; a.click(); URL.revokeObjectURL(url);
      toast.success(`Token file generated for ${u.name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate"); }
  };

  const revoke = async (u) => {
    try { await api.post(`/auth/users/${u.id}/revoke`); toast.success("Token revoked. Existing key files are now invalid."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not revoke"); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Settings" subtitle="Currencies, exchange rates and access keys" />
      <Tabs defaultValue="fx">
        <TabsList className="mb-5">
          <TabsTrigger value="fx" data-testid="tab-fx"><Coins className="h-4 w-4 mr-2" /> Exchange Rates</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users"><Users className="h-4 w-4 mr-2" /> Users & Keys</TabsTrigger>
        </TabsList>

        <TabsContent value="fx">
          <Card className="bg-white border-border p-6 max-w-3xl">
            <h3 className="text-sm font-semibold text-[var(--ink)]">Currency conversion to INR</h3>
            <p className="text-sm text-muted-foreground mt-1 mb-5">Set the rate at which each currency converts to your INR base. Foreign holdings are valued using these rates.</p>
            {loading ? <Skeleton className="h-64 rounded-lg" /> : (
              <div className="grid sm:grid-cols-2 gap-4">
                {fx.map((r) => (
                  <div key={r.currency} className="flex items-center gap-3 rounded-lg border border-border p-3">
                    <span className="flex h-10 w-12 items-center justify-center rounded-md bg-[var(--sand)] font-semibold text-sm">{r.currency}</span>
                    <div className="flex-1">
                      <Label className="text-xs text-muted-foreground">1 {r.currency} = ₹</Label>
                      <Input type="number" defaultValue={r.rate_to_inr} disabled={r.currency === "INR"}
                        onBlur={(e) => r.currency !== "INR" && Number(e.target.value) !== r.rate_to_inr && updateFx(r.currency, e.target.value)}
                        className="mt-1 font-data" data-testid={`fx-input-${r.currency}`} />
                    </div>
                    {savingFx === r.currency && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-4">Rates are reference values you control — update them periodically to keep valuations accurate.</p>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card className="bg-white border-border overflow-hidden max-w-4xl">
            <div className="flex items-center justify-between p-5 pb-3">
              <div>
                <h3 className="text-sm font-semibold text-[var(--ink)]">Vault Access</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Enrol users and issue their encrypted pendrive key files.</p>
              </div>
              {isAdmin && <Button onClick={() => setEnrollOpen(true)} className="bg-[var(--primary-deep-green)] hover:bg-[var(--primary-deep-green-hover)] text-white" data-testid="enroll-user-button"><Plus className="h-4 w-4 mr-2" /> Enrol User</Button>}
            </div>
            {loading ? <Skeleton className="h-40 m-5 rounded-lg" /> : (
              <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="users-table"><thead className="bg-[var(--sand)]/60"><tr className="text-left text-xs font-semibold text-slate-600"><th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Role</th><th className="px-5 py-3 text-right">Key Actions</th></tr></thead>
                <tbody>{users.map((u) => (<tr key={u.id} className="border-t border-border hover:bg-[var(--sand)]/40">
                  <td className="px-5 py-3 font-medium text-[var(--ink)]">{u.name}{u.id === user?.id && <span className="ml-2 text-[11px] text-muted-foreground">(you)</span>}</td>
                  <td className="px-5 py-3 text-slate-600">{u.email}</td>
                  <td className="px-5 py-3"><Badge variant="secondary" className="capitalize">{u.role}</Badge></td>
                  <td className="px-5 py-3"><div className="flex items-center justify-end gap-1">
                    <Button variant="outline" size="sm" onClick={() => provision(u)} data-testid={`provision-${u.id}`}><Download className="h-3.5 w-3.5 mr-1" /> Issue Key</Button>
                    {isAdmin && <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-[var(--destructive)]" data-testid={`revoke-${u.id}`}><ShieldOff className="h-3.5 w-3.5 mr-1" /> Revoke</Button></AlertDialogTrigger><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Revoke access key?</AlertDialogTitle><AlertDialogDescription>All existing key files for {u.name} will stop working. They will need a freshly issued key to sign in.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => revoke(u)} className="bg-[var(--destructive)] hover:bg-[var(--destructive)]/90">Revoke</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>}
                  </div></td>
                </tr>))}</tbody></table></div>
            )}
          </Card>

          <Dialog open={enrollOpen} onOpenChange={setEnrollOpen}>
            <DialogContent className="max-w-md"><DialogHeader><DialogTitle className="font-display">Enrol User</DialogTitle><DialogDescription>Add a person who can access this vault with their own pendrive key.</DialogDescription></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5"><Label>Full Name</Label><Input value={enrollForm.name} onChange={(e) => setEnrollForm({ ...enrollForm, name: e.target.value })} data-testid="enroll-name-input" /></div>
                <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={enrollForm.email} onChange={(e) => setEnrollForm({ ...enrollForm, email: e.target.value })} data-testid="enroll-email-input" /></div>
                <div className="space-y-1.5"><Label>Role</Label><Select value={enrollForm.role} onValueChange={(v) => setEnrollForm({ ...enrollForm, role: v })}><SelectTrigger data-testid="enroll-role-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="admin">Admin — full control</SelectItem><SelectItem value="editor">Editor — manage data</SelectItem><SelectItem value="viewer">Viewer — read only</SelectItem></SelectContent></Select></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setEnrollOpen(false)}>Cancel</Button><Button onClick={enroll} disabled={busy} className="bg-[var(--primary-deep-green)] hover:bg-[var(--primary-deep-green-hover)] text-white" data-testid="enroll-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-2" /> Enrol</>}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}
